import { useNavigate, useRouter } from '@tanstack/react-router';
import { Modal } from 'components/Modal/Modal';
import VerifyOtp from 'components/Otp/VerifyOtp/VerifyOtp';
import useOtpStatus from 'hooks/useOtpStatus';
import useResetOtpTimeout from 'hooks/useResetOtpTimeout';
import { useUpdatePassword } from 'hooks/useUserProfile';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store/reducer';
import { otpProcesses } from 'utils/constants';
import AskForTwoFactor from './AskForTwoFactor';
import ChangePassword from './ChangePassword';
import allMessages from './messages';
import Success from './Success';
import { UpdatePasswordState } from './types/UpdatePasswordState';

export const UpdatePasswordComponent = () => {
  const otpStatus = useOtpStatus();
  const navigate = useNavigate();
  const router = useRouter();
  const updatePassword = useUpdatePassword();
  const resetTimestamp = useResetOtpTimeout();

  const { language } = useSelector(
    (rootState: RootState) => rootState.language,
  );

  const [state, setState] = useState<UpdatePasswordState>({});
  const [passwordValid, setPasswordValid] = useState(false);

  function validatePasswordState() {
    if (!state.oldPassword) return true;
    if (!passwordValid) return true;
    return false;
  }

  const messages = allMessages[language];

  function goToPortfolio() {
    navigate({ to: '/portfolio' });
  }

  function showSuccess() {
    setState({ ...state, showModal: true, loading: false });
  }

  function changePassword() {
    if (state.newPassword === state.oldPassword) {
      setState({
        ...state,
        showError: true,
        errorMessage: messages.passwordsSameError,
      });
    } else
      setState({
        ...state,
        askForTwoFactor: true,
        showError: false,
        errorMessage: '',
      });
  }

  function changePasswordWithTwoFactor(pin?: string) {
    setState({ ...state, showError: false, errorMessage: '', loading: true });

    updatePassword({
      existingPassword: state.oldPassword || '',
      newPassword: state.newPassword || '',
      totp: typeof pin === 'string' ? pin : state.totp || '',
      callback: response => {
        if (response.success) {
          if (otpStatus !== 'TOTP') {
            resetTimestamp(
              otpStatus === 'SMS' ? 'SMS' : 'EMAIL',
              otpProcesses.PROFILE_CHANGE_PASSWORD,
            );
          }
          showSuccess();
        } else
          setState({
            ...state,
            showError: true,
            errorMessage: response?.message?.message || response?.message,
            loading: false,
          });
      },
    });
  }

  const twoFactorContent = () => {
    switch (otpStatus) {
      case 'TOTP':
        return (
          <AskForTwoFactor
            checkTwoFactor={changePasswordWithTwoFactor}
            isTwoFactorCodeValid={!state.totpInvalid}
            onTwoFactorChanged={(value: string) =>
              setState({ ...state, totp: value, totpInvalid: false })
            }
          />
        );
      case 'SMS':
      case 'EMAIL':
      default:
        return (
          <VerifyOtp
            title={messages.otp.title}
            onChangeOtp={changePasswordWithTwoFactor}
            type={otpStatus}
            error={
              state?.showError && state?.errorMessage?.includes('Invalid OTP')
            }
            isLoading={state.loading}
            removeErrorFunction={() =>
              setState({ ...state, showError: false, errorMessage: '' })
            }
            process={otpProcesses.PROFILE_CHANGE_PASSWORD}
          />
        );
    }
  };

  return (
    <div className="static lg:relative w-full">
      <button
        type="button"
        onClick={() => router.history.back()}
        className="absolute lg:fixed mt-2 ml-6 text-2xl lg:mt-10 lg:ml-6 lg:text-base left-0 top-0 lg:top-auto lg:left-auto z-40 text-header-text focus:outline-none font-semibold">
        <span className="block lg:hidden">←</span>
        <span className="hidden lg:block">{`← ${messages.backButton}`}</span>
      </button>
      <div className="max-w-2xl mx-auto px-4 pt-0 lg:pt-16 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          {state.askForTwoFactor ? (
            twoFactorContent()
          ) : (
            <ChangePassword
              loading={state.loading}
              updatePasswordState={state}
              buttonDisabled={validatePasswordState() || state.loading}
              oldPasswordChanged={(value: string) =>
                setState({ ...state, oldPassword: value })
              }
              newPasswordChanged={(value: string) =>
                setState({ ...state, newPassword: value })
              }
              confirmPasswordChanged={(value: string) =>
                setState({ ...state, confirmNewPassword: value })
              }
              passwordValidationChanged={(value: boolean) =>
                setPasswordValid(value)
              }
              onContinue={changePassword}
            />
          )}
        </div>
        {state?.showError === true &&
        !state?.errorMessage?.includes('Invalid OTP') ? (
          <div className="mt-6">
            <span className="block text-center text-red-700">
              {state.errorMessage}
            </span>
          </div>
        ) : null}
        <Modal
          hideModal={() => goToPortfolio()}
          content={<Success onContinue={goToPortfolio} />}
          closeModal={() => goToPortfolio()}
          show={state.showModal || false}
        />
      </div>
    </div>
  );
};
